import { mountVueWithPinia, mountVue } from '../vue/mountVue';
import Cookies from 'js-cookie';
import { audienceLibrary, waitForDYObject } from '@utils/global-functions';
import {
  determineRecommendation,
  dyAPIKey,
  formatDyExperienceAPIData,
} from '@utils/ProductResources';
import ProductGrid from '../vue/ProductCards/ProductGrid.vue';
import ProductSlider from '../vue/ProductCards/ProductSlider.vue';

const passJsonAsProp = async (el, content) => {
  const data = JSON.parse(content) ?? false;
  if (!data) return;
  //using DY experience API data
  if (data.variations)
    return el.setAttribute(
      'props-data',
      JSON.stringify(formatDyExperienceAPIData(data))
    );

  //using in-house product card data
  const recommendation = data?.recommendation ?? false;
  if (!recommendation) return el.setAttribute('props-data', content);

  //using DYO recommendation widget data
  const limit = data?.limit_recommendations ?? false;
  data.items = await determineRecommendation(recommendation, limit);
  return el.setAttribute('props-data', JSON.stringify(data));
};

export class ProductGrids extends HTMLElement {
  constructor(context) {
    super();
    if (context) this.mountProductCards(context);
  }
  async mountProductCards(context) {
    //if the in page navigation module exists on a landing page or PLP, prevent auto scrolling to the top of the page on load
    if (!document.querySelector('.page-navigation')) window.scrollTo(0, 0);

    const app = context.querySelector('.js-product-grid');
    const jsonScript = context.querySelector('.js-product-grid-json');
    if (!app || !jsonScript) return;
    await passJsonAsProp(app, jsonScript.innerHTML);
    mountVueWithPinia('.js-product-grid', ProductGrid, context);
  }
}

//Call the DY choose endpoint once per page load, if there are DY recommendation sections on the page
document.addEventListener('DOMContentLoaded', function () {
  const dySliders = document.querySelectorAll(
    '[data-ht-module="dy-recommendation-slider"]'
  );
  if (dySliders.length > 0) {
    loadDYRecommendationSliders(dySliders);
  }
});
export class ProductSliders extends HTMLElement {
  constructor(context) {
    super();
    if (context) this.mountProductCards(context);
  }
  async mountProductCards(context) {
    const app = context.querySelector('.js-product-slider');
    //if the product slider is powered by DY audiences, wait for the audience DY data to come in, then grab the correct audience specific JSON to mount Vue.
    if (context.classList.contains('audience-strategies')) {
      waitForDYObject()
        .then((obj) => {
          const matchedAudience = audienceLibrary.filter((audience) =>
            obj.audiences.includes(audience.id)
          )?.[0];
          //if DY does not load, just default recommendations to the low intent strategy
          const audienceId = matchedAudience.index || 1;
          return context.querySelector(
            `.js-product-slider-json[data-audience="${audienceId}"]`
          );
        })
        .then((recommendationData) => {
          mountDYAudienceRecommendations(app, context, recommendationData);
        });
    } else if (!app.classList.contains('js-dy-product-slider')) {
      //mount product cards regularly
      const jsonScript = context.querySelector('.js-product-slider-json');
      if (!app || !jsonScript) return;
      await passJsonAsProp(app, jsonScript.innerHTML);
      mountVueWithPinia('.js-product-slider', ProductSlider, context);
    }
  }
}

const mountDYAudienceRecommendations = async (app, context, jsonScript) => {
  await passJsonAsProp(app, jsonScript.innerHTML);
  mountVueWithPinia('.js-product-slider', ProductSlider, context);
};

const getDYOptions = () => {
  const now = new Date();
  return {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'dy-api-key': dyAPIKey,
    },
    body: JSON.stringify({
      user: {
        dyid: Cookies.get('_dyid'),
        dyid_server: Cookies.get('_dyid_server'),
      },
      session: {
        dy: Cookies.get('_dyjsession') || window.DY.session,
      },
      context: {
        page: {
          type: window.DY.recommendationContext.type,
          data: window.DY.recommendationContext.data || [],
          location: window.location.href,
          locale: navigator.language,
        },
        device: {
          userAgent: navigator.userAgent,
          browser: window.DY.deviceInfo?.browser?.toUpperCase(),
          dateTime: now.toISOString(),
        },
      },
      selector: { groups: ['DY Recommendations'] },
      options: {
        recsProductData: {
          fieldFilter: ['shopify_product_sku', 'group_id'],
        },
      },
    }),
  };
};

const loadDYRecommendationSliders = (dySliders) => {
  fetch('https://direct.dy-api.com/v2/serve/user/choose', getDYOptions())
    .then((res) => res.json())
    .then((res) => {
      //after fetching all dy recommendation slider data for the page, loop through each section
      dySliders.forEach(async (slider) => {
        //find each dy recommendation slider's specific product data, then pass it as a JSON prop to the product slider
        const recommendationData = res.choices.find(
          (choice) => choice.name == slider.getAttribute('data-ht-id')
        );
        await passJsonAsProp(
          slider.querySelector('.js-product-slider'),
          JSON.stringify(recommendationData)
        );
        mountVueWithPinia('.js-product-slider', ProductSlider, slider);
      });
    })
    .catch(async (err) => {
      console.log(
        'error loading dy recommendations. loading backup from theme settings. error: ',
        err
      );
      //use backup data specified in section if there is an error
      dySliders.forEach(async (slider) => {
        const backupData = slider.querySelector(
          '.dy-recommendations-backup-json'
        );
        await passJsonAsProp(
          slider.querySelector('.js-product-slider'),
          backupData.innerHTML
        );
        mountVueWithPinia('.js-product-slider', ProductSlider, slider);
      });
    });
};
customElements.define('product-grid', ProductGrids);
customElements.define('product-slider', ProductSliders);
